import React, { forwardRef } from 'react';

const FacturePrint = forwardRef((props, ref) => {
    const {
        factureData = {},
        company = {}
    } = props;

    const client = factureData.client || factureData.client_info || {};
    const hebergement = factureData.hebergement || factureData.chambre || {};
    const rawLignesChambres = Array.isArray(factureData.lignes_chambres) ? factureData.lignes_chambres : [];
    const restauration = Array.isArray(factureData.restauration) ? factureData.restauration : [];
    const extras = Array.isArray(factureData.extras) ? factureData.extras : [];

    const hasRestauration = restauration.length > 0;
    const totalRestaurationsTTC = restauration.reduce((acc, item) => acc + Number(item.montant_ttc || 0), 0);
    const totalRestaurationsHT = restauration.reduce((acc, item) => acc + Number(item.montant_ht || 0), 0);
    const totalRestaurationsTVA = restauration.reduce((acc, item) => acc + Number(item.montant_tva || 0), 0);

    const hasExtras = extras.length > 0;
    const totalExtrasTTC = extras.reduce((acc, item) => acc + Number(item.montant_ttc || 0), 0);
    const totalExtrasHT = extras.reduce((acc, item) => acc + Number(item.montant_ht || 0), 0);
    const totalExtrasTVA = extras.reduce((acc, item) => acc + Number(item.montant_tva || 0), 0);

    const montantHebergementGlobalTTC = Number(hebergement.montant_total || hebergement.montant || 0);
    
    // Totaux finaux garantis
    const totalTTC = Number(factureData.montant_ttc || (montantHebergementGlobalTTC + totalRestaurationsTTC + totalExtrasTTC) || 0);
    
    // Vérification si l'entreprise est assujettie à la TVA
    const isTvaActive = Number(company.regime_tva_recuperable ?? 1) === 1 && Number(company.taux_tva ?? 18.0) > 0;
    const tauxTvaApplique = Number(company.taux_tva || 18.0);

    // Si pas de TVA, le HT est égal au TTC et la TVA est à 0
    const sousTotalHT = isTvaActive ? Number(factureData.montant_ht !== undefined ? factureData.montant_ht : (hebergement.montant_ht || 0) + totalRestaurationsHT + totalExtrasHT) : totalTTC;
    const montantTVA = isTvaActive ? Number(factureData.montant_tva !== undefined ? factureData.montant_tva : (hebergement.montant_tva || 0) + totalRestaurationsTVA + totalExtrasTVA) : 0;
    
    const estSolde = Boolean(factureData.est_solde || factureData.solde || factureData.paye);
    const numeroFacture = factureData.facture_no || factureData.numero || 'FAC-' + Date.now().toString().slice(-6);

    // --- CONSTRUCTION DES DONNÉES POUR LE QR CODE ---
    const qrDataPayload = JSON.stringify({
        hotel: company.name || company.nom || "HOTEL CHAINE B",
        facture: numeroFacture,
        date: factureData.date ? new Date(factureData.date).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR'),
        client: client.nom_complet || client.nom || client.name || "CLIENT AU COMPTANT",
        montant_ttc: `${totalTTC} FCFA`,
        statut: estSolde ? "SOLDÉ" : "NON SOLDÉ"
    });
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(qrDataPayload)}`;

    // --- MOTEUR DE CONVERSION EN TOUTES LETTRES ---
    const formaterMontantEnLettres = (montantInput) => {
        const nombre = Math.round(montantInput || 0);
        if (nombre === 0) return "Arrêtée la présente facture à la somme de : Zéro (0) Francs CFA.";

        const unites = ['', 'un', 'deux', 'trois', 'quatre', 'cinq', 'six', 'sept', 'huit', 'neuf'];
        const dizaines = ['', 'dix', 'vingt', 'trente', 'quarante', 'cinquante', 'soixante', '', 'quatre-vingt', 'quatre-vingt-dix'];
        const nombresSpeciaux = {
            11: 'onze', 12: 'douze', 13: 'treize', 14: 'quatorze', 15: 'quinze', 16: 'seize',
            71: 'soixante-onze', 72: 'soixante-douze', 73: 'soixante-treize', 74: 'soixante-quatorze',
            75: 'soixante-quinze', 76: 'soixante-seize', 91: 'quatre-vingt-onze', 92: 'quatre-vingt-douze',
            93: 'quatre-vingt-treize', 94: 'quatre-vingt-quatorze', 95: 'quatre-vingt-quinze', 96: 'quatre-vingt-seize'
        };

        function convertirGroupe(n) {
            let str = '';
            const c = Math.floor(n / 100);
            const r = n % 100;
            const d = Math.floor(r / 10);
            const u = r % 10;

            if (c > 0) {
                str += (c === 1 ? '' : unites[c] + ' ') + 'cent' + (c > 1 && r === 0 ? 's' : '') + ' ';
            }

            if (nombresSpeciaux[r]) {
                str += nombresSpeciaux[r];
            } else {
                if (d > 0) {
                    if (d === 7 || d === 9) {
                        str += dizaines[d - 1] + '-' + nombresSpeciaux[r - (d - 1) * 10];
                    } else {
                        str += dizaines[d] + (u === 1 && d !== 8 ? '-et-' : (u > 0 ? '-' : ''));
                    }
                }
                if (u > 0 && !(d === 7 || d === 9)) {
                    str += unites[u];
                }
            }
            return str.trim();
        }

        let resultat = '';
        let reste = nombre;

        const milliards = Math.floor(reste / 1000000000); reste %= 1000000000;
        const millions = Math.floor(reste / 1000000); reste %= 1000000;
        const milliers = Math.floor(reste / 1000); reste %= 1000;

        if (milliards > 0) resultat += convertirGroupe(milliards) + ' milliard' + (milliards > 1 ? 's' : '') + ' ';
        if (millions > 0) resultat += convertirGroupe(millions) + ' million' + (millions > 1 ? 's' : '') + ' ';
        if (milliers > 0) resultat += (milliers === 1 ? '' : convertirGroupe(milliers) + ' ') + 'mille ';
        if (reste > 0) resultat += convertirGroupe(reste);

        const lettres = resultat.replace(/\s+/g, ' ').trim();
        if (!lettres) return `Arrêtée la présente facture à la somme de : Zéro (${nombre}) Francs CFA.`;

        const premiereLettre = lettres.substring(0, 1).toUpperCase();
        const resteDuTexte = lettres.substring(1);
        
        return `Arrêtée la présente facture à la somme de : ${premiereLettre + resteDuTexte} (${new Intl.NumberFormat('fr-FR').format(nombre)}) Francs CFA.`;
    };

    const getLogoSrc = () => {
        const data = company.logo_data || company.logo || company.logo_url;
        if (!data) return null;
        if (data.startsWith('data:') || data.startsWith('http') || data.startsWith('blob:')) {
            return data;
        }
        return `data:image/png;base64,${data}`;
    };

    const logoSrc = getLogoSrc();
    const estGroupe = rawLignesChambres.length > 1;

    return (
        <div 
            ref={ref} 
            className="facture-hebergement-page"
            style={{
                fontFamily: '"Segoe UI", Arial, sans-serif',
                color: '#1e293b',
                background: '#fff',
                padding: '15px',
                boxSizing: 'border-box',
                width: '100%',
                maxWidth: '210mm',
                margin: '0 auto',
                fontSize: '11px',
                lineHeight: 1.4,
                display: 'flex',
                flexDirection: 'column',
                minHeight: '277mm'
            }}
        >
            <style>
                {`
                    @media print {
                        @page {
                            size: A4 portrait !important;
                            margin: 10mm !important;
                        }
                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 100% !important;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .facture-hebergement-page {
                            width: 190mm !important;
                            max-width: 190mm !important;
                            margin: 0 auto !important;
                            background: #fff !important;
                            box-sizing: border-box !important;
                            min-height: 277mm !important;
                        }
                    }
                `}
            </style>

            <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '2px solid #1e40af', paddingBottom: '12px', marginBottom: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {logoSrc ? (
                            <img src={logoSrc} alt="Logo" style={{ maxHeight: '16mm', maxWidth: '40mm', objectFit: 'contain' }} />
                        ) : (
                            <div style={{ width: '45px', height: '45px', background: '#1e40af', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px', borderRadius: '4px' }}>
                                {(company.name || company.nom || 'H').charAt(0)}
                            </div>
                        )}
                        <div>
                            <h2 style={{ color: '#1e40af', margin: '0 0 2px 0', fontSize: '15px', fontWeight: 'bold' }}>
                                {company.name || company.nom || "HÔTEL CHAÎNE B"}
                            </h2>
                            <p style={{ margin: '1px 0', color: '#64748b', fontSize: '10px' }}>{company.address || company.adresse || "Adresse de l'hôtel"}</p>
                            <p style={{ margin: '1px 0', color: '#64748b', fontSize: '10px' }}>Tél: {company.phone || company.telephone || "N/A"} | Email: {company.email || "N/A"}</p>
                        </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <h1 style={{ color: '#1e40af', margin: '0 0 4px 0', fontSize: '18px', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                            {estGroupe ? "FACTURE GROUPE / HÉBERGEMENT" : "FACTURE D'HÉBERGEMENT"}
                        </h1>
                        <p style={{ margin: '2px 0', fontWeight: 'bold', fontSize: '11px' }}>
                            N° : {numeroFacture}
                        </p>
                        <p style={{ margin: '2px 0', color: '#475569', fontSize: '10px' }}>
                            Date : {factureData.date ? new Date(factureData.date).toLocaleDateString('fr-FR') : new Date().toLocaleDateString('fr-FR')}
                        </p>
                        {estSolde ? (
                            <div style={{ marginTop: '6px', display: 'inline-block', background: '#dcfce7', color: '#15803d', border: '1px solid #bbf7d0', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '10px', textTransform: 'uppercase' }}>
                                Statut : Soldé
                            </div>
                        ) : (
                            <div style={{ marginTop: '6px', display: 'inline-block', background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold', fontSize: '10px', textTransform: 'uppercase' }}>
                                Statut : Non soldé
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '10px 12px' }}>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 'bold', fontSize: '10px', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
                            Client / Facturé à :
                        </div>
                        <div style={{ fontSize: '12px', fontWeight: 'bold', color: '#0f172a', marginBottom: '2px' }}>
                            {client.nom_complet || client.nom || client.name || "CLIENT AU COMPTANT"}
                        </div>
                        {client.client_occupant && (
                            <div style={{ color: '#1e40af', fontSize: '10.5px', fontWeight: 'bold', margin: '2px 0' }}>
                                Client occupant : {client.client_occupant}
                            </div>
                        )}
                        <div style={{ color: '#334155', fontSize: '10.5px', margin: '1px 0' }}>
                            NIF / IFU : {client.nif || client.ifu || "0"}
                        </div>
                        <div style={{ color: '#334155', fontSize: '10.5px', margin: '1px 0' }}>
                            Adresse : {client.adresse || client.address || "Non renseignée"}
                        </div>
                        <div style={{ color: '#334155', fontSize: '10.5px', margin: '1px 0' }}>
                            Téléphone : {client.telephone || client.phone || "Non renseigné"}
                        </div>
                    </div>
                    <div style={{ flex: '0 0 40%', borderLeft: '1px solid #cbd5e1', paddingLeft: '12px' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '10px', color: '#475569', textTransform: 'uppercase', marginBottom: '4px' }}>
                            Séjour & Réservation :
                        </div>
                        <div style={{ color: '#334155', fontSize: '10.5px', margin: '2px 0' }}>
                            <strong>Arrivée :</strong> {hebergement.date_arrivee ? new Date(hebergement.date_arrivee).toLocaleDateString('fr-FR') : '---'}
                        </div>
                        <div style={{ color: '#334155', fontSize: '10.5px', margin: '2px 0' }}>
                            <strong>Départ :</strong> {hebergement.date_depart ? new Date(hebergement.date_depart).toLocaleDateString('fr-FR') : '---'}
                        </div>
                        <div style={{ color: '#334155', fontSize: '10.5px', margin: '2px 0' }}>
                            <strong>Type :</strong> {estGroupe ? `Groupe (${rawLignesChambres.length} chambres)` : `Chambre ${hebergement.chambre_nom || 'N/A'}`}
                        </div>
                    </div>
                </div>

                {/* Tableau principal des prestations */}
                <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '15px' }}>
                    <thead>
                        <tr style={{ background: '#1e40af', color: '#fff' }}>
                            <th style={{ padding: '8px 10px', textAlign: 'left', fontSize: '10.5px', fontWeight: 'bold', width: '50%' }}>DÉSIGNATION</th>
                            <th style={{ padding: '8px 10px', textAlign: 'center', fontSize: '10.5px', fontWeight: 'bold', width: '15%' }}>UNITÉS</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10.5px', fontWeight: 'bold', width: '15%' }}>P.U. (FCFA)</th>
                            <th style={{ padding: '8px 10px', textAlign: 'right', fontSize: '10.5px', fontWeight: 'bold', width: '20%' }}>MONTANT (FCFA)</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rawLignesChambres.length > 0 ? (
                            rawLignesChambres.map((ligne, idx) => (
                                <tr key={`chambre-print-${idx}`} style={{ borderBottom: '1px solid #e2e8f0' }}>
                                    <td style={{ padding: '8px 10px', textAlign: 'left' }}>
                                        <div style={{ fontWeight: 'bold', color: '#0f172a' }}>
                                            {ligne.designation || `Hébergement - Chambre ${ligne.chambre_nom || ligne.chambre} (${ligne.occupant_nom || client.nom_complet || 'Client'})`}
                                        </div>
                                        <div style={{ fontSize: '9.5px', color: '#64748b' }}>Séjour hôtelier</div>
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 'bold' }}>
                                        {ligne.nombre_nuitees || ligne.nuites || 1} Nuitée(s)
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>
                                        {Number(ligne.prix_unitaire || 0).toLocaleString()}
                                    </td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace' }}>
                                        {Number(ligne.montant_total || ligne.montant_ttc || 0).toLocaleString()} F
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
                                <td style={{ padding: '8px 10px', textAlign: 'left' }}>
                                    <div style={{ fontWeight: 'bold', color: '#0f172a' }}>
                                        {hebergement.designation || `Hébergement - Chambre ${hebergement.chambre_nom || 'A1'}`}
                                    </div>
                                    <div style={{ fontSize: '9.5px', color: '#64748b' }}>Séjour hôtelier</div>
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 'bold' }}>
                                    {hebergement.nombre_nuites || 1} Nuitée(s)
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>
                                    {Number(hebergement.prix_unitaire || montantHebergementGlobalTTC).toLocaleString()}
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace' }}>
                                    {montantHebergementGlobalTTC.toLocaleString()} F
                                </td>
                            </tr>
                        )}

                        {hasRestauration && restauration.map((item, index) => (
                            <tr key={`restau-${index}`} style={{ borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
                                <td style={{ padding: '8px 10px', textAlign: 'left' }}>
                                    <div style={{ fontWeight: 'bold', color: '#0f172a' }}>{item.designation || "Consommation Bar / Restauration"}</div>
                                    <div style={{ fontSize: '9.5px', color: '#64748b' }}>Restauration / Bar</div>
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 'bold' }}>{item.quantite || 1}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{Number(item.prix_unitaire || item.montant_ttc || 0).toLocaleString()}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace' }}>
                                    {Number(item.montant_ttc || 0).toLocaleString()} F
                                </td>
                            </tr>
                        ))}

                        {hasExtras && extras.map((item, index) => (
                            <tr key={`extra-${index}`} style={{ borderBottom: '1px solid #e2e8f0', background: '#f5f3ff' }}>
                                <td style={{ padding: '8px 10px', textAlign: 'left' }}>
                                    <div style={{ fontWeight: 'bold', color: '#0f172a' }}>{item.designation || "Prestation Extra"}</div>
                                    <div style={{ fontSize: '9.5px', color: '#64748b' }}>Prestation Extra / Service</div>
                                </td>
                                <td style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 'bold' }}>{item.quantite || 1}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontFamily: 'monospace' }}>{Number(item.prix_unitaire || item.montant_ttc || 0).toLocaleString()}</td>
                                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 'bold', fontFamily: 'monospace' }}>
                                    {Number(item.montant_ttc || 0).toLocaleString()} F
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginTop: '10px', gap: '15px' }}>
                    <div style={{ flex: 1, background: '#f1f5f9', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                        <div style={{ fontWeight: 'bold', fontSize: '10px', color: '#334155', marginBottom: '4px', textTransform: 'uppercase' }}>
                            Arrêté de la facture :
                        </div>
                        <p style={{ fontStyle: 'italic', fontWeight: 'bold', color: '#1e40af', margin: 0, fontSize: '11px' }}>
                            {formaterMontantEnLettres(totalTTC)}
                        </p>
                    </div>

                    <div style={{ width: '45%' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                <tr>
                                    <td style={{ padding: '4px 8px', fontSize: '10.5px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>TOTAL H.T.</td>
                                    <td style={{ padding: '4px 8px', fontSize: '10.5px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold', borderBottom: '1px solid #e2e8f0' }}>
                                        {sousTotalHT.toLocaleString()} F
                                    </td>
                                </tr>
                                
                                {isTvaActive && (
                                    <tr>
                                        <td style={{ padding: '4px 8px', fontSize: '10.5px', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>T.V.A. ({tauxTvaApplique}%)</td>
                                        <td style={{ padding: '4px 8px', fontSize: '10.5px', textAlign: 'right', fontFamily: 'monospace', fontWeight: 'bold', borderBottom: '1px solid #e2e8f0' }}>
                                            + {montantTVA.toLocaleString()} F
                                        </td>
                                    </tr>
                                )}

                                <tr style={{ background: '#1e40af', color: '#fff' }}>
                                    <td style={{ padding: '6px 8px', fontSize: '11.5px', fontWeight: 'bold', textAlign: 'left' }}>NET À PAYER</td>
                                    <td style={{ padding: '6px 8px', fontSize: '12px', fontWeight: 'bold', textAlign: 'right', fontFamily: 'monospace' }}>
                                        {totalTTC.toLocaleString()} FCFA
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Bloc Signatures avec QR Code d'Authentification Intégré */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '30px', padding: '0 10px' }}>
                    <div style={{ textAlign: 'center', width: '180px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '35px', color: '#1e293b', textTransform: 'uppercase' }}>LE CLIENT / LA SOCIÉTÉ</div>
                        <div style={{ borderTop: '1px dashed #64748b', paddingTop: '4px', fontSize: '9px', color: '#64748b', fontStyle: 'italic' }}>Signature et cachet</div>
                    </div>

                    {/* QR Code de vérification d'authenticité */}
                    <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <img 
                            src={qrCodeUrl} 
                            alt="QR Code Authenticité Facture" 
                            style={{ width: '65px', height: '65px', objectFit: 'contain', border: '1px solid #cbd5e1', padding: '2px', background: '#fff', borderRadius: '4px' }} 
                        />
                        <span style={{ fontSize: '8px', color: '#64748b', fontWeight: 'bold', marginTop: '3px', textTransform: 'uppercase' }}>Authenticité Séjour</span>
                    </div>

                    <div style={{ textAlign: 'center', width: '180px' }}>
                        <div style={{ fontSize: '10px', fontWeight: 'bold', marginBottom: '35px', color: '#1e293b', textTransform: 'uppercase' }}>LA RÉCEPTION</div>
                        <div style={{ borderTop: '1px dashed #64748b', paddingTop: '4px', fontSize: '9px', color: '#64748b', fontStyle: 'italic' }}>Signature et cachet</div>
                    </div>
                </div>

                {/* Message d'information client */}
                <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '10px', color: '#1e40af', fontStyle: 'italic', background: '#eff6ff', padding: '6px 12px', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                    ✨ Merci d'avoir séjourné parmi nous ! Pour votre sécurité financière, veuillez toujours exiger votre facture portant la mention officielle de statut <strong>SOLDÉ</strong> après chaque règlement.
                </div>
            </div>

            {/* Pied de page PROPRE SANS URL (Suppression du lien en bas de page) */}
            <div style={{ marginTop: 'auto', paddingTop: '15px', borderTop: '1px solid #cbd5e1', textAlign: 'center', fontSize: '9.5px', color: '#64748b' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{company.name || company.nom || "HÔTEL CHAÎNE B"}</div>
                <div>{company.address || company.adresse || "Adresse de l'hôtel"} — Tél : {company.phone || company.telephone || "N/A"} {company.email ? `— E-mail : ${company.email}` : ''}</div>
            </div>
        </div>
    );
});

FacturePrint.displayName = 'FacturePrint';
export default FacturePrint;